import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { AuthLayout } from './AuthLayout';
import { authService } from '../../services/authService';
import { useAuth } from '../../context/AuthContext';
import { sanitizeEmail } from '../../utils/inputSanitizer';

const ALLOWED_GROUPS = ['Admin', 'UserManager'];

const landingFor = (groups: string[]) => (groups.includes('Admin') ? '/dashboard' : '/users');

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);

  // An already-valid session should land on its home page instead of the form.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const user = await authService.getCurrentUser();
        if (cancelled || !user) return;
        if (ALLOWED_GROUPS.some((group) => user.groups.includes(group))) {
          navigate(landingFor(user.groups), { replace: true });
        } else {
          await authService.logout();
          localStorage.clear();
          sessionStorage.clear();
        }
      } catch {
        localStorage.clear();
        sessionStorage.clear();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Drop any stale session first so a failed login cannot leave the old one.
      try {
        await authService.logout();
      } catch {
        /* no session to clear */
      }

      const user = await authService.login({ email, password });

      if (!ALLOWED_GROUPS.some((group) => user.groups.includes(group))) {
        await authService.logout();
        localStorage.clear();
        sessionStorage.clear();
        setError('This account has no console access. Ask an administrator for the Admin or User Manager role.');
        emailRef.current?.focus();
        return;
      }

      await refresh();
      navigate(landingFor(user.groups), { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign in failed. Check your details and try again.');
      emailRef.current?.focus();
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Sign in"
      subtitle="Use the account your administrator issued for the AgentX Manager."
      footer={
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Need an account?{' '}
          <Link component={RouterLink} to="/signup">
            Request access
          </Link>
        </Typography>
      }
    >
      {error && (
        <Alert severity="error" role="alert" sx={{ mb: 2.5 }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Stack spacing={2.25}>
          <TextField
            inputRef={emailRef}
            fullWidth
            size="medium"
            label="Email"
            type="email"
            autoComplete="username"
            autoFocus
            value={email}
            onChange={(event) => setEmail(sanitizeEmail(event.target.value))}
            required
            disabled={loading}
          />

          <TextField
            fullWidth
            size="medium"
            label="Password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
            disabled={loading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword((prev) => !prev)}
                    edge="end"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <VisibilityOffOutlinedIcon fontSize="small" />
                    ) : (
                      <VisibilityOutlinedIcon fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading || !email || !password}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{ mt: 0.5 }}
          >
            {loading ? 'Signing in…' : 'Sign in'}
          </Button>
        </Stack>
      </Box>
    </AuthLayout>
  );
};

export default Login;
